/**
 * electric auth login
 *
 * Opens browser for OAuth login. Starts a local HTTP server to receive
 * the callback with the JWT.
 *
 * Note: The server-side OAuth endpoints are implemented in Phase C.
 * This command will work once those endpoints are deployed.
 */

import { Command } from 'commander'
import { randomUUID } from 'node:crypto'
import { URL } from 'node:url'
import { saveStoredAuth } from '../../config.js'
import { getDashboardUrl, CLI_VERSION } from '../../client.js'
import { handleError } from '../../lib/errors.js'
import { printSuccess } from '../../output.js'
import { startLocalServer } from '../../lib/local-server.js'
import { capture, flush } from '../../telemetry.js'

const LOGIN_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

export const loginCommand = new Command(`login`)
  .description(`Log in to Electric Cloud via browser`)
  .action(async () => {
    const json = loginCommand.parent?.parent?.opts().json ?? false
    try {
      await runLogin(json)
    } catch (error) {
      await handleError(error, json)
    }
  })

interface AuthCallbackResult {
  token: string
  email?: string
  expiresAt?: string
}

async function runLogin(json: boolean): Promise<void> {
  const state = randomUUID()

  // Start local server on ephemeral port
  const { port, resultPromise, shutdown } =
    await startLocalServer<AuthCallbackResult>({
      callbackPath: `/callback`,
      parseParams: (params) => {
        const receivedState = params.get(`state`)
        if (receivedState !== state) {
          throw new Error(`State mismatch in OAuth callback`)
        }
        const token = params.get(`token`)
        if (!token) {
          throw new Error(`No token received in OAuth callback`)
        }
        return {
          token,
          email: params.get(`email`) ?? undefined,
          expiresAt: params.get(`expiresAt`) ?? undefined,
        }
      },
      successHtml: `<html><body><p>Authentication successful. You can close this tab.</p><script>window.close()</script></body></html>`,
      errorHtml: `<html><body><p>Authentication failed. Please try again.</p></body></html>`,
    })

  // Build login URL — open the dashboard login page with CLI params
  const loginUrl = new URL(`/`, getDashboardUrl())
  loginUrl.searchParams.set(`cli_port`, String(port))
  loginUrl.searchParams.set(`cli_state`, state)

  // Open browser
  try {
    const open = (await import(`open`)).default
    await open(loginUrl.toString())
    if (!json) {
      process.stderr.write(
        `Opening browser for login...\nIf the browser doesn't open, visit:\n${loginUrl.toString()}\n`
      )
    }
  } catch {
    process.stderr.write(
      `Could not open browser. Visit this URL to log in:\n${loginUrl.toString()}\n`
    )
  }

  // Wait for callback or timeout
  let timeoutId: ReturnType<typeof setTimeout>
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      shutdown()
      reject(
        new Error(
          `Authentication timed out after ${LOGIN_TIMEOUT_MS / 60_000} minutes. Run 'electric auth login' to try again.`
        )
      )
    }, LOGIN_TIMEOUT_MS)
  })

  try {
    const result = await Promise.race([resultPromise, timeoutPromise])
    clearTimeout(timeoutId!)
    shutdown()

    await saveStoredAuth({
      token: result.token,
      email: result.email ?? `unknown`,
      expiresAt:
        result.expiresAt ??
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    })

    capture({
      event: `cli_auth_login`,
      properties: { cli_version: CLI_VERSION },
    })

    if (json) {
      process.stdout.write(
        JSON.stringify({ success: true, email: result.email }) + `\n`
      )
    } else {
      printSuccess(`Logged in successfully.`)
    }
  } catch (error) {
    const reason =
      error instanceof Error && error.message.includes(`timed out`)
        ? `timeout`
        : error instanceof Error && error.message.includes(`State mismatch`)
          ? `state_mismatch`
          : error instanceof Error && error.message.includes(`No token`)
            ? `no_token`
            : `unknown`
    capture({
      event: `cli_auth_login_failed`,
      properties: { cli_version: CLI_VERSION, reason },
    })
    await flush()
    shutdown()
    throw error
  }
}
